<template>
  <div class="account-home">
    <header class="account-home-header">
      <div>
        <div class="account-home-kicker">Account Dashboard</div>
        <h1>Welcome back, {{ firstName }}</h1>
        <p>Review your saved PCs, continue a build, or manage your account.</p>
      </div>
      <router-link class="account-primary-action" to="/builder">
        <span class="icon icon-tools"></span>
        Start a new build
      </router-link>
    </header>

    <section class="account-overview-grid">
      <article class="account-identity-card">
        <div class="account-avatar">
          <img
            v-if="avatarUrl"
            :src="avatarUrl"
            :alt="displayName"
            referrerpolicy="no-referrer"
            @error="avatarError = true"
          >
          <span v-else>{{ initials }}</span>
        </div>
        <div class="account-identity-copy">
          <span class="account-card-label">Your account</span>
          <h2>{{ displayName }}</h2>
          <p>{{ user.email || 'No email on file' }}</p>
          <span v-if="user.username" class="account-username">@{{ user.username }}</span>
        </div>
        <router-link class="account-text-action" to="/users/settings">Manage settings</router-link>
      </article>

      <article class="account-stat-card">
        <span class="account-card-label">Saved builds</span>
        <strong>{{ buildCount }}</strong>
        <p>{{ buildCount ? completeBuildCount + ' complete parts list' + (completeBuildCount === 1 ? '' : 's') : 'Your build library is ready when you are.' }}</p>
        <router-link to="/users/mybuild">View all builds <span aria-hidden="true">&rsaquo;</span></router-link>
      </article>

      <article class="account-stat-card account-stat-card--accent">
        <span class="account-card-label">Next step</span>
        <strong>{{ buildCount ? 'Keep building' : 'Create your first PC' }}</strong>
        <p>{{ buildCount ? 'Open a saved build to compare or update its parts.' : 'Choose compatible components and save the result to your account.' }}</p>
        <router-link :to="buildCount ? '/users/mybuild' : '/builder'">
          {{ buildCount ? 'Choose a build' : 'Open the builder' }} <span aria-hidden="true">&rsaquo;</span>
        </router-link>
      </article>
    </section>

    <section class="account-builds-panel">
      <div class="account-panel-heading">
        <div>
          <span class="account-card-label">Build library</span>
          <h2>Recent builds</h2>
        </div>
        <router-link v-if="buildCount" class="account-secondary-action" to="/users/mybuild">View all</router-link>
      </div>

      <LoadingOverlay v-if="!ifBuildsLoaded" mode="block" min-height="250px" />

      <div v-else-if="recentBuilds.length" class="account-build-list">
        <article v-for="build in recentBuilds" :key="build.id" class="account-build-row">
          <div class="account-build-image" :class="{ 'is-empty': !build.image }">
            <img v-if="build.image" :src="build.image" :alt="build.name">
            <span v-else class="icon icon-tools"></span>
          </div>
          <div class="account-build-copy">
            <div class="account-build-title-line">
              <h3>{{ build.name }}</h3>
              <span class="account-build-status" :class="{ complete: build.complete }">
                {{ build.complete ? 'Complete' : 'In progress' }}
              </span>
            </div>
            <p>{{ build.partCount }} of {{ requiredPartCount }} core parts selected</p>
            <div class="account-build-progress" aria-hidden="true">
              <span :style="{ width: build.progress + '%' }"></span>
            </div>
          </div>
          <button type="button" class="account-open-build" @click="openBuild(build.raw)">
            Continue
            <span aria-hidden="true">&rsaquo;</span>
          </button>
        </article>
      </div>

      <div v-else class="account-empty-builds">
        <span class="icon icon-tools"></span>
        <div>
          <h3>No saved builds yet</h3>
          <p>Start with any component. GixCore will keep your parts together as the build takes shape.</p>
        </div>
        <router-link class="account-primary-action" to="/builder">Start building</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BUILD_PARTS } from '../../partOrder'
import LoadingOverlay from '../widgets/LoadingOverlay.vue'

export default {
  name: 'AccountHome',
  components: { LoadingOverlay },
  data () {
    return {
      avatarError: false,
      lastFetchedUserId: null
    }
  },
  computed: {
    ...mapGetters(['getUser', 'getUserBuilds', 'ifBuildsLoaded']),
    user () {
      if (this.getUser && typeof this.getUser === 'object') return this.getUser
      if (typeof this.getUser === 'string') {
        try { return JSON.parse(this.getUser) || {} } catch (e) { return {} }
      }
      return {}
    },
    firstName () {
      return this.user.first_name || this.user.username || 'builder'
    },
    displayName () {
      const name = [this.user.first_name, this.user.last_name].filter(Boolean).join(' ')
      return name || this.user.username || 'GixCore User'
    },
    initials () {
      return (this.displayName || this.user.email || 'GC')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    avatarUrl () {
      return this.avatarError ? '' : (this.user.img_url || '')
    },
    builds () {
      return Array.isArray(this.getUserBuilds) ? this.getUserBuilds : []
    },
    buildCount () {
      return this.builds.length
    },
    requiredPartCount () {
      return BUILD_PARTS.length
    },
    enrichedBuilds () {
      return this.builds
        .map(build => this.summarizeBuild(build))
        .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
    },
    recentBuilds () {
      return this.enrichedBuilds.slice(0, 3)
    },
    completeBuildCount () {
      return this.enrichedBuilds.filter(build => build.complete).length
    }
  },
  watch: {
    getUser: {
      immediate: true,
      handler () {
        this.fetchBuilds()
      }
    }
  },
  methods: {
    ...mapActions(['fetchUserBuilds', 'loadBuild']),
    fetchBuilds () {
      const userId = this.user.id
      if (!userId || this.lastFetchedUserId === userId) return
      this.lastFetchedUserId = userId
      this.fetchUserBuilds(userId)
    },
    hasPart (value) {
      return Array.isArray(value) ? value.length > 0 : !!value
    },
    summarizeBuild (build) {
      const partCount = BUILD_PARTS.filter(part => this.hasPart(build[part.backendKey])).length
      return {
        id: build.id,
        raw: build,
        name: build.name || 'Saved build',
        image: this.buildImage(build),
        partCount,
        complete: partCount === this.requiredPartCount,
        progress: Math.round((partCount / this.requiredPartCount) * 100)
      }
    },
    buildImage (build) {
      if (build.image) return build.image
      const preferred = ['case', 'processor', 'graphics', 'motherboard']
      for (let i = 0; i < preferred.length; i += 1) {
        const item = build[preferred[i]]
        const candidate = Array.isArray(item) ? item[0] : item
        if (!candidate || !candidate.imgUrl) continue
        try {
          const images = JSON.parse(candidate.imgUrl)
          if (Array.isArray(images) && images.length) return images[0]
        } catch (e) { /* use the next available part */ }
      }
      return ''
    },
    openBuild (build) {
      this.loadBuild(build)
    }
  }
}
</script>

<style scoped>
.account-home {
  width: 100%;
  max-width: 1160px;
  padding: 0.25rem 0 3rem;
  color: #cfd2da;
}

.account-home-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.15rem;
  padding-bottom: 1.15rem;
  border-bottom: 1px solid rgba(92, 101, 120, 0.62);
}

.account-home-kicker,
.account-card-label {
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.09em;
  line-height: 1;
  text-transform: uppercase;
}

.account-home-header h1 {
  margin: 0.45rem 0 0.35rem;
  color: #edf2f7;
  font-size: clamp(25px, 3vw, 34px);
  font-weight: 500;
  line-height: 1.12;
}

.account-home-header p,
.account-stat-card p,
.account-identity-copy p,
.account-build-copy p,
.account-empty-builds p {
  margin: 0;
  color: #919bad;
  font-size: 13px;
  line-height: 1.5;
}

.account-primary-action,
.account-secondary-action,
.account-open-build {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 38px;
  padding: 0 0.9rem;
  border: 1px solid rgba(25, 151, 198, 0.76);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #50c5ed;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
  text-transform: uppercase;
  transition: 160ms ease;
}

.account-primary-action:hover,
.account-secondary-action:hover,
.account-open-build:hover {
  border-color: #28b9eb;
  background: rgba(25, 151, 198, 0.18);
  color: #fff;
  text-decoration: none;
}

.account-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) repeat(2, minmax(210px, 0.8fr));
  gap: 0.8rem;
}

.account-identity-card,
.account-stat-card,
.account-builds-panel {
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 35%),
    rgba(10, 13, 20, 0.72);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025), 0 16px 42px rgba(0, 0, 0, 0.17);
}

.account-identity-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.9rem 1rem;
  align-items: center;
  padding: 1.15rem;
}

.account-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 62px;
  height: 62px;
  border: 1px solid rgba(25, 151, 198, 0.82);
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(25, 151, 198, 0.24), rgba(8, 10, 15, 0.88));
  color: #eef7fb;
  font-size: 20px;
  font-weight: 800;
}

.account-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.account-identity-copy {
  min-width: 0;
}

.account-identity-copy h2 {
  margin: 0.38rem 0 0.18rem;
  color: #edf2f7;
  font-size: 20px;
  font-weight: 600;
}

.account-identity-copy p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-username {
  display: block;
  margin-top: 0.25rem;
  color: #68768c;
  font-size: 12px;
}

.account-text-action {
  grid-column: 2;
  justify-self: start;
  color: #42bde7;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.account-stat-card {
  display: flex;
  flex-direction: column;
  min-height: 170px;
  padding: 1.1rem;
}

.account-stat-card strong {
  margin: 0.7rem 0 0.35rem;
  color: #edf2f7;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.05;
}

.account-stat-card--accent strong {
  color: #5dcaee;
  font-size: 20px;
  line-height: 1.2;
}

.account-stat-card a {
  margin-top: auto;
  padding-top: 0.9rem;
  color: #42bde7;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.account-builds-panel {
  margin-top: 0.85rem;
  padding: 1.1rem;
}

.account-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.account-panel-heading h2 {
  margin: 0.38rem 0 0;
  color: #e7ecf3;
  font-size: 20px;
  font-weight: 500;
}

.account-secondary-action {
  min-height: 32px;
  background: transparent;
}

.account-build-list {
  display: grid;
  gap: 0.55rem;
}

.account-build-row {
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.9rem;
  padding: 0.75rem;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 7px;
  background: rgba(7, 10, 16, 0.54);
}

.account-build-image {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 66px;
  height: 54px;
  border-radius: 6px;
  background: #e6ebf1;
}

.account-build-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.account-build-image.is-empty {
  border: 1px solid rgba(67, 72, 87, 0.7);
  background: rgba(14, 18, 27, 0.8);
  color: #1997c6;
}

.account-build-copy {
  min-width: 0;
}

.account-build-title-line {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.account-build-title-line h3 {
  overflow: hidden;
  margin: 0;
  color: #e7ecf3;
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-build-status {
  flex: 0 0 auto;
  padding: 0.18rem 0.45rem;
  border: 1px solid rgba(205, 184, 91, 0.44);
  border-radius: 999px;
  color: #d6c87a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.account-build-status.complete {
  border-color: rgba(27, 201, 142, 0.42);
  color: #65d5ad;
}

.account-build-progress {
  width: min(310px, 100%);
  height: 3px;
  margin-top: 0.5rem;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(67, 72, 87, 0.66);
}

.account-build-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #147ca4, #22b9a2);
}

.account-open-build {
  min-height: 34px;
  background: transparent;
}

.account-empty-builds {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  min-height: 150px;
  padding: 1rem 1.2rem;
  border: 1px dashed rgba(67, 82, 105, 0.78);
  border-radius: 8px;
  background: rgba(6, 9, 14, 0.38);
}

.account-empty-builds > .icon {
  color: #1997c6;
  font-size: 28px;
}

.account-empty-builds h3 {
  margin: 0 0 0.3rem;
  color: #e7ecf3;
  font-size: 17px;
  font-weight: 500;
}

@media (max-width: 920px) {
  .account-overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .account-identity-card {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .account-home-header {
    align-items: stretch;
    flex-direction: column;
  }

  .account-home-header .account-primary-action {
    width: 100%;
  }

  .account-overview-grid {
    grid-template-columns: 1fr;
  }

  .account-identity-card {
    grid-column: auto;
  }

  .account-stat-card {
    min-height: 150px;
  }

  .account-build-row {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .account-build-image {
    width: 52px;
    height: 48px;
  }

  .account-open-build {
    grid-column: 1 / -1;
    width: 100%;
  }

  .account-build-title-line {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.3rem;
  }

  .account-empty-builds {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .account-empty-builds > .icon {
    justify-self: center;
  }
}
</style>
